// WHAT MONEY ACTUALLY MOVED, MONTH BY MONTH — one fold per partner, one row per month,
// and each month opening onto the engagements the money moved on.
//
// A DIFFERENT QUESTION FROM THE GAP ABOVE IT, and the separation is deliberate. The gap
// is cumulative and has no monthly value — entitlement is a share of profit over all
// time, so there is no "gap this month" and this table never pretends there is. What it
// shows is cash: what each partner took in and what each paid out, in the month it
// actually happened.
//
// THE FOOT IS STILL THE POINT. The server asserts that a partner's months sum to their
// cumulative collected and paid — the same two figures the gap's own cash side uses —
// before it sends them, and this table prints those totals as its last row. Everything
// here is display: the client re-adds nothing.
import { useState } from 'react';
import he from '../../i18n/he';
import Collapsible from '../../components/Collapsible';
import DataTable from '../../components/DataTable';
import Money, { Cost } from '../../components/Money';
import DateText from '../../components/DateText';

const t = he.settlements.monthly;
// What a unit is CALLED, from the block shared with the collected list above and the
// dashboard's top-units tile. These three labels used to hang off the gap's per-unit
// breakdown, which this view never had anything to do with; that table is gone and they
// were never its property in the first place.
const unitLabel = he.settlements.unit;

// Signed and coloured the way the gap's rows are: a month where more came in than went
// out reads positive. Zero stays plain.
const Signed = ({ value }) => {
  const tone = Math.abs(value) < 0.005 ? '' : (value > 0 ? 'pos' : 'neg');
  return <Money value={value} className={tone} />;
};

// The engagements one month's money moved on, shown beneath the table for the selected
// row. The office bucket belongs to no unit and carries its own label rather than a badge.
const UnitLines = ({ month, units }) => (
  <div className="settle-units">
    <div className="settle-units-head">
      {t.unitsFor} <DateText value={month} />
    </div>
    {units.map((u) => (
      <div className="settle-unit-row" key={u.unit}>
        <span>
          {u.unit === 'office' ? (
            <strong>{t.office}</strong>
          ) : (
            <>
              <span className={`badge ${u.kind === 'retainer' ? 'blue' : 'gray'}`}>
                {u.kind === 'retainer' ? unitLabel.retainer : unitLabel.job}
              </span>{' '}
              {u.title || unitLabel.unnamed}
              {u.clientName && <span className="muted"> · {u.clientName}</span>}
            </>
          )}
        </span>
        <span className="settle-unit-figures">
          {t.collectedShort} <Money value={u.collected} /> · {t.paidShort} <Cost value={u.paid} />
        </span>
      </div>
    ))}
  </div>
);

export default function MonthlyCashflow({ partner, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [openMonth, setOpenMonth] = useState(null);

  const columns = [
    { key: 'month', label: t.month, primary: true,
      render: (r) => (r.foot ? <strong>{t.total}</strong> : <DateText value={r.month} />) },
    { key: 'collected', label: t.collected, className: 'num', render: (r) => <Money value={r.collected} /> },
    { key: 'paid', label: t.paid, className: 'num', render: (r) => <Cost value={r.paid} /> },
    { key: 'net', label: t.net, className: 'num', render: (r) => <Signed value={r.net} /> },
  ];

  const selected = partner.months.find((m) => m.month === openMonth);

  const rows = [
    ...partner.months.map((m) => ({ ...m, id: m.month })),
    // The foot, printed from the server's cumulative figures — the ones it just proved
    // the months add up to, not a sum of the rows above.
    {
      id: '__total', foot: true,
      collected: partner.totalCollected,
      paid: partner.totalPaid,
      net: Math.round((partner.totalCollected - partner.totalPaid) * 100) / 100,
    },
  ];

  return (
    <Collapsible
      open={open}
      onToggle={() => setOpen((s) => !s)}
      title={partner.name}
      badges={<Signed value={Math.round((partner.totalCollected - partner.totalPaid) * 100) / 100} />}
    >
      {partner.months.length === 0 ? (
        <p className="muted small-note">{t.empty}</p>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={rows}
            scroll
            rowClass={(r) => [r.foot ? 'row-foot' : '',
              !r.foot && openMonth === r.month ? 'row-selected' : ''].filter(Boolean).join(' ')}
            onRowClick={(r) => (r.foot ? null : setOpenMonth(openMonth === r.month ? null : r.month))}
          />
          {selected && selected.units.length > 0 && (
            <UnitLines month={selected.month} units={selected.units} />
          )}
        </>
      )}
    </Collapsible>
  );
}
